import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { useNavigate } from 'react-router-dom';
import { authService } from '../api/authService';  // ✅ Importar authService
import {
    CONFIGURACION_INACTIVIDAD,
    TIEMPO_ACTIVIDAD_CALCULADO
} from '@/config/configuracion';

/**
 * Hook para detectar inactividad del usuario y cerrar sesión automáticamente
 * 
 * Configuración desde configuracion.ts:
 * - TIEMPO_INACTIVIDAD_TOTAL: 10 minutos
 * - ADVERTENCIA_ANTES_LOGOUT_INACTIVIDAD: X segundos (configurable)
 * - A los (15min - Xs) → Muestra modal warning con countdown
 * - Usuario puede cancelar con cualquier actividad o click botón
 * - A los 15:00 → Logout automático si no hay actividad
 */
export const useRastreadorActividad = () => {
    const { isAuthenticated, logout } = useAuthStore();
    const navigate = useNavigate();
    const timeoutActividadRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const timeoutLogoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [mostrarAdvertencia, setMostrarAdvertencia] = useState(false);
    // Calcular countdown inicial desde configuración (ms → segundos)
    const COUNTDOWN_INICIAL_SEGUNDOS = Math.floor(
        CONFIGURACION_INACTIVIDAD.ADVERTENCIA_ANTES_LOGOUT_INACTIVIDAD / 1000
    );
    const [cuentaRegresiva, setCuentaRegresiva] = useState(COUNTDOWN_INICIAL_SEGUNDOS);

    const manejarLogout = async () => {
        if (!isAuthenticated) return;

        setMostrarAdvertencia(false);

        // ✅ FIX: Llamar endpoint logout para registrar evento en BD
        try {
            await authService.logout();
        } catch (error) {
            console.error('Error al registrar logout por inactividad:', error);
        }

        logout();  // Logout local
        navigate('/login', { replace: true });
    };

    const cancelarAdvertencia = () => {
        // Usuario canceló advertencia → resetear timeouts
        setMostrarAdvertencia(false);
        setCuentaRegresiva(10);

        if (timeoutLogoutRef.current) {
            clearTimeout(timeoutLogoutRef.current);
            timeoutLogoutRef.current = null;
        }

        // Limpiar timeouts
        if (timeoutLogoutRef.current) {
            clearTimeout(timeoutLogoutRef.current);
        }

        // Resetear estado
        setMostrarAdvertencia(false);
        setCuentaRegresiva(COUNTDOWN_INICIAL_SEGUNDOS);  // ✔️ Usar config

        // Reiniciar timer de actividad
        resetearTimeoutActividad();
    };

    const resetearTimeoutActividad = () => {
        // Limpiar timeouts existentes
        if (timeoutActividadRef.current) {
            clearTimeout(timeoutActividadRef.current);
        }
        if (timeoutLogoutRef.current) {
            clearTimeout(timeoutLogoutRef.current);
        }

        // Cerrar advertencia si estaba abierta
        setMostrarAdvertencia(false);
        setCuentaRegresiva(COUNTDOWN_INICIAL_SEGUNDOS);

        // Configurar nuevo timeout de actividad (usando TIEMPO_ACTIVIDAD_CALCULADO)
        timeoutActividadRef.current = setTimeout(() => {
            // Mostrar advertencia modal
            setMostrarAdvertencia(true);

            // Configurar timeout de logout (ADVERTENCIA_ANTES_LOGOUT_INACTIVIDAD)
            timeoutLogoutRef.current = setTimeout(() => {
                manejarLogout();
            }, CONFIGURACION_INACTIVIDAD.ADVERTENCIA_ANTES_LOGOUT_INACTIVIDAD);
        }, TIEMPO_ACTIVIDAD_CALCULADO);
    };

    // Cuenta regresiva en tiempo real cuando advertencia está visible
    useEffect(() => {
        if (!mostrarAdvertencia) return;

        const intervalo = setInterval(() => {
            setCuentaRegresiva(prev => {
                if (prev <= 1) {
                    clearInterval(intervalo);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(intervalo);
    }, [mostrarAdvertencia]);

    useEffect(() => {
        if (!isAuthenticated) {
            // Limpiar todos los timeouts si no autenticado
            if (timeoutActividadRef.current) {
                clearTimeout(timeoutActividadRef.current);
                timeoutActividadRef.current = null;
            }
            if (timeoutLogoutRef.current) {
                clearTimeout(timeoutLogoutRef.current);
                timeoutLogoutRef.current = null;
            }
            setMostrarAdvertencia(false);
            return;
        }

        const eventos = CONFIGURACION_INACTIVIDAD.EVENTOS_ACTIVIDAD;

        const manejarActividad = () => {
            if (mostrarAdvertencia) {
                // Si advertencia está visible, cualquier actividad la cancela
                cancelarAdvertencia();
            } else {
                // Si no hay advertencia, solo resetear timeout normal
                resetearTimeoutActividad();
            }
        };

        eventos.forEach((evento) => {
            window.addEventListener(evento, manejarActividad, { passive: true });
        });

        resetearTimeoutActividad();

        return () => {
            eventos.forEach((evento) => {
                window.removeEventListener(evento, manejarActividad);
            });

            if (timeoutActividadRef.current) {
                clearTimeout(timeoutActividadRef.current);
            }
            if (timeoutLogoutRef.current) {
                clearTimeout(timeoutLogoutRef.current);
            }
        };
    }, [isAuthenticated]); // ✅ FIX: Removido showWarning para evitar re-registro listeners

    // API pública (mantener nombres inglés para compatibilidad DashboardLayout)
    return {
        showWarning: mostrarAdvertencia,
        countdown: cuentaRegresiva,
        onContinueSession: cancelarAdvertencia,
    };
};

